// @flow
import { reduce, makeActionCreator } from '../../helpers/utils';
import ApiClient from '../../api/Api';
import type { State as GlobalState } from '../reducer';
import { parseServerSideValidation } from '../../helpers/validations/serverSideValidation';
import { showSnackbar } from '../snackbar';
import type { ClientAction } from '../../types';

export const LOAD_SUCCESS = 'profile/LOAD_SUCCESS';
export const UPDATE_FIRST_NAME = 'profile/UPDATE_FIRST_NAME';
export const UPDATE_LAST_NAME = 'profile/UPDATE_LAST_NAME';
export const UPDATE_EMAIL = 'profile/UPDATE_EMAIL';
export const UPDATE_PHONE = 'profile/UPDATE_PHONE';
export const UPDATE_COUNTRY = 'profile/UPDATE_COUNTRY';
export const SET_RESOLVED_ADDRESS = 'profile/SET_RESOLVED_ADDRESS';
export const UPDATE_ADDRESS = 'profile/UPDATE_ADDRESS';
export const SET_RESOLVED_ADDRESS_ERROR = 'profile/SET_RESOLVED_ADDRESS_ERROR';
export const SET_VALIDATION_ERRORS = 'profile/SET_VALIDATION_ERRORS';
export const SUBMIT_DETAILS_STARTED = 'profile/SUBMIT_DETAILS_STARTED';
export const SUBMIT_DETAILS_SUCCESS = 'profile/SUBMIT_DETAILS_SUCCESS';
export const SUBMIT_DETAILS_FAIL = 'profile/SUBMIT_DETAILS_FAIL';
export const SUBMIT_EMAIL_STARTED = 'profile/SUBMIT_EMAIL_STARTED';
export const SUBMIT_EMAIL_FAIL = 'profile/SUBMIT_EMAIL_FAIL';
export const SUBMIT_EMAIL_SUCCESS = 'profile/SUBMIT_EMAIL_SUCCESS';
export const SET_EDITING_EMAIL = 'profile/SET_EDITING_EMAIL';
export const EMAIL_VERIFICATION_REQUEST_SENT =
  'profile/EMAIL_VERIFICATION_REQUEST_SENT';
export const EMAIL_VERIFICATION_REQUEST_SUCCESS =
  'profile/EMAIL_VERIFICATION_REQUEST_SUCCESS';
export const EMAIL_VERIFICATION_REQUEST_FAIL =
  'profile/EMAIL_VERIFICATION_REQUEST_FAIL';
export const SET_PASSWORD = 'profile/SET_PASSWORD';
export const RESET_PASSWORD_LINK_SENT = 'profile/RESET_PASSWORD_LINK_SENT';
export const RESET_PASSWORD_LINK_SUCCESS =
  'profile/RESET_PASSWORD_LINK_SUCCESS';
export const RESET_PASSWORD_LINK_FAILED = 'profile/RESET_PASSWORD_LINK_FAILED';
export const SUBMIT_ADDRESS_STARTED = 'profile/SUBMIT_ADDRESS_STARTED';
export const SUBMIT_ADDRESS_SUCCESS = 'profile/SUBMIT_ADDRESS_SUCCESS';
export const SUBMIT_ADDRESS_FAIL = 'profile/SUBMIT_ADDRESS_FAIL';
export const RESET_PASSWORD_SENT = 'profile/RESET_PASSWORD_SENT';
export const RESET_PASSWORD_SUCCESS = 'profile/RESET_PASSWORD_SUCCESS';
export const RESET_PASSWORD_FAIL = 'profile/RESET_PASSWORD_FAIL';

const debug = require('debug')('jp:reducers:profile');

type Action = { type: typeof LOAD_SUCCESS };

export type Address = {
  line1: string,
  line2: ?string,
  city: string,
  country: string,
  postcode: string,
  addressId: ?string,
  country: string,
  city: string,
  address: ?string
};

export type User = {
  firstName: string,
  lastName: string,
  phone: string,
  email: string,
  newEmail?: string,
  hasVerifiedEmail: boolean,
  hasVerifiedPhoneNumber: boolean,
  address: ?Address,
  password: ?string
};

export type State = {
  user: User,
  error: string,
  isLoading: boolean,
  emailSubmitting: boolean,
  showSubmitAddress: boolean,
  submitingAddress: boolean,
  editingEmail: boolean,
  resettingPassword: boolean,
  formError: {
    firstName: string,
    lastName: string,
    address: string,
    phone: string,
    email: string,
    password: string
  }
};

export const initialState = {
  error: '',
  isLoading: false,
  user: {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    hasVerifiedEmail: true,
    hasVerifiedPhoneNumber: false,
    address: null,
    password: '',
    newEmail: ''
  },
  formError: {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address: '',
    password: '',
    newEmail: ''
  },
  loading: false,
  emailSubmitting: false,
  editingEmail: false,
  showSubmitAddress: false,
  submitingAddress: false,
  resettingPassword: false
};

const emailErrors = payload => {
  if (payload.error === 1019) {
    return { password: payload.message };
  }
  return parseServerSideValidation(payload);
};

const getAddressError = response => {
  if (response.error === 2000) {
    return Object.values(response.meta).join(', ');
  }

  return 'Something went wrong';
};

export default reduce(initialState, {
  [LOAD_SUCCESS](state: State, action): State {
    debug(LOAD_SUCCESS, action);
    return {
      ...state,
      user: action.payload
    };
  },
  [SUBMIT_DETAILS_STARTED](state: State): State {
    return {
      ...state,
      isLoading: true,
      formError: initialState.formError
    };
  },
  [SUBMIT_DETAILS_SUCCESS](state: State): State {
    return {
      ...state,
      isLoading: false
    };
  },
  [SUBMIT_DETAILS_FAIL](state: State, action): State {
    return {
      ...state,
      isLoading: false,
      formError: {
        ...initialState.formError,
        ...parseServerSideValidation(action.payload)
      }
    };
  },
  [SET_EDITING_EMAIL](state: State, action): State {
    return {
      ...state,
      user: {
        ...state.user,
        password: '',
        newEmail: ''
      },
      formError: {
        ...state.formError,
        password: '',
        newEmail: '',
        email: ''
      },
      editingEmail: action.payload
    };
  },
  [SUBMIT_EMAIL_STARTED](state: State): State {
    return {
      ...state,
      emailSubmitting: true,
      formError: initialState.formError
    };
  },
  [SUBMIT_EMAIL_FAIL](state: State, action): State {
    return {
      ...state,
      emailSubmitting: false,
      formError: {
        ...initialState.formError,
        ...emailErrors(action.payload)
      }
    };
  },
  [SUBMIT_EMAIL_SUCCESS](state: State): State {
    return {
      ...state,
      emailSubmitting: false,
      editingEmail: false
    };
  },
  [SET_VALIDATION_ERRORS](state: State, action): State {
    debug(SET_VALIDATION_ERRORS, action);
    return {
      ...state,
      formError: {
        ...initialState.formError,
        ...action.payload
      }
    };
  },
  [EMAIL_VERIFICATION_REQUEST_FAIL](state: State, action): State {
    return {
      ...state,
      error: action.payload
    };
  },
  [RESET_PASSWORD_SENT](state: State): State {
    return {
      ...state,
      resettingPassword: true
    };
  },
  [RESET_PASSWORD_SUCCESS](state: State): State {
    return {
      ...state,
      resettingPassword: false
    };
  },
  [RESET_PASSWORD_FAIL](state: State, action): State {
    return {
      ...state,
      resettingPassword: false,
      error: action.payload?.data?.title
    };
  },
  [LOAD_SUCCESS](state: State, action): State {
    debug(LOAD_SUCCESS, action);
    return {
      ...state,
      user: action.payload
    };
  },
  [UPDATE_FIRST_NAME](state: State, action): State {
    debug(UPDATE_FIRST_NAME, action);
    return {
      ...state,
      user: {
        ...state.user,
        firstName: action.payload
      }
    };
  },
  [UPDATE_LAST_NAME](state: State, action): State {
    debug(UPDATE_LAST_NAME, action);
    return {
      ...state,
      user: {
        ...state.user,
        lastName: action.payload
      }
    };
  },
  [UPDATE_EMAIL](state: State, action): State {
    debug(UPDATE_EMAIL, action);
    return {
      ...state,
      user: {
        ...state.user,
        newEmail: action.payload
      }
    };
  },
  [UPDATE_PHONE](state: State, action): State {
    debug(UPDATE_PHONE, action);
    return {
      ...state,
      user: {
        ...state.user,
        phone: action.payload
      }
    };
  },
  [SET_PASSWORD](state: State, action): State {
    debug(SET_PASSWORD, action);
    return {
      ...state,
      user: {
        ...state.user,
        password: action.payload
      }
    };
  },
  [UPDATE_COUNTRY](state: State, action): State {
    debug(UPDATE_COUNTRY, action);
    return {
      ...state,
      user: {
        ...state.user,
        address: {
          ...state.user.address,
          country: action.payload
        }
      }
    };
  },
  [UPDATE_ADDRESS](state: State, action): State {
    const { addressId } = action;
    return {
      ...state,
      user: {
        ...state.user,
        address: {
          ...state.user.address,
          addressId
        }
      }
    };
  },
  [SET_RESOLVED_ADDRESS](state: State, action): State {
    const {
      houseNo,
      address1,
      address2,
      address3,
      city,
      postalCode,
      country,
      state: addressState
    } = action.addressData;
    return {
      ...state,
      showSubmitAddress: true,
      user: {
        ...state.user,
        address: {
          ...state.user.address,
          addressResolved: true,
          houseNo,
          line1: address1,
          line2: address2,
          line3: address3,
          city,
          postcode: postalCode,
          // In case resolve failed do not reset the country from the drop down
          country: country || state.user.address.country,
          state: addressState
        }
      },
      formError: {
        ...state.formError,
        address: ''
      }
    };
  },
  [SET_RESOLVED_ADDRESS_ERROR](state: State): State {
    return {
      ...state,
      showSubmitAddress: false,
      user: {
        ...state.user,
        address: null
      },
      formError: {
        ...state.formError,
        address:
          'Please ensure you have selected your full address including house name/number'
      }
    };
  },
  [SUBMIT_ADDRESS_SUCCESS](state: State): State {
    return {
      ...state,
      showSubmitAddress: false,
      submitingAddress: false,
      formError: {
        ...state.formError,
        address: ''
      }
    };
  },
  [SUBMIT_ADDRESS_STARTED](state: State): State {
    return {
      ...state,
      showSubmitAddress: false,
      submitingAddress: true
    };
  },
  [SUBMIT_ADDRESS_FAIL](state: State, action): State {
    return {
      ...state,
      formError: {
        ...initialState.formError,
        address: getAddressError(action.payload)
      },
      submitingAddress: false
    };
  }
});

export const sendResetPasswordLink = () => {
  return (
    dispatch: (ClientAction | Action) => void,
    getState: () => GlobalState
  ) => {
    const { email } = getState().auth.user;
    dispatch({
      types: [RESET_PASSWORD_SENT, RESET_PASSWORD_SUCCESS, RESET_PASSWORD_FAIL],
      promise: (client: ApiClient) => client.resetPassword(email),
      onSuccessDispatched: () => {
        dispatch(showSnackbar('Password reset link has been emailed to you'));
      },
      onFailureDispatched: () => {
        dispatch(showSnackbar('Error: Reset password email not sent'));
      }
    });
  };
};

export function load() {
  return (dispatch: Action => void, getState: () => GlobalState) => {
    const {
      auth: {
        user: {
          firstName,
          lastName,
          phone,
          email,
          address,
          hasVerifiedEmail,
          hasVerifiedPhoneNumber
        }
      }
    } = getState();

    return dispatch({
      type: LOAD_SUCCESS,
      payload: {
        firstName,
        lastName,
        phone,
        email,
        address,
        hasVerifiedEmail,
        hasVerifiedPhoneNumber
      }
    });
  };
}

export const updateFirstName = makeActionCreator(UPDATE_FIRST_NAME, 'payload');
export const updateLastName = makeActionCreator(UPDATE_LAST_NAME, 'payload');
export const updateEmail = makeActionCreator(UPDATE_EMAIL, 'payload');
export const updatePhone = makeActionCreator(UPDATE_PHONE, 'payload');
export const updateCountry = makeActionCreator(UPDATE_COUNTRY, 'payload');
export const setAddress = makeActionCreator(UPDATE_ADDRESS, 'addressId');
export const setValidationErrors = makeActionCreator(
  SET_VALIDATION_ERRORS,
  'payload'
);
export const setPassword = makeActionCreator(SET_PASSWORD, 'payload');
export const setResolvedAddress = makeActionCreator(
  SET_RESOLVED_ADDRESS,
  'payload',
  'addressData'
);
export const setResolvedAddressError = makeActionCreator(
  SET_RESOLVED_ADDRESS_ERROR
);
export const setEditingEmail = makeActionCreator(SET_EDITING_EMAIL, 'payload');

const validateEmailData = (user: $PropertyType<State, 'user'>) => {
  const errors = [];
  if (!user.password || user.password.trim().length === 0) {
    errors.push({ password: 'Password is a required field' });
  }
  if (typeof user.newEmail === 'string' && user.newEmail.trim().length === 0) {
    errors.push({ newEmail: 'Email is a required field' });
  }
  return errors;
};

const validatePersonalDetails = (user: $PropertyType<State, 'user'>) => {
  const errors = [];

  if (user.firstName.trim().length === 0) {
    errors.push({ firstName: 'First name is a required field' });
  }

  if (user.lastName.trim().length === 0) {
    errors.push({ lastName: 'Last name is a required field' });
  }
  return errors;
};

const getErrors = errors => {
  if (errors.length > 0) {
    return errors.reduce((acc, item) => {
      return {
        ...acc,
        ...item
      };
    }, {});
  }
  return null;
};

export const submitDetails = () => {
  return (
    dispatch: (ClientAction | Action) => void,
    getState: () => GlobalState
  ) => {
    const { profile } = getState();
    const { firstName, lastName, phone } = profile.user;

    const errors = validatePersonalDetails(profile.user);
    const errorMessages = getErrors(errors);

    if (errorMessages) {
      dispatch(setValidationErrors(errorMessages));
      return;
    }
    dispatch({
      types: [
        SUBMIT_DETAILS_STARTED,
        SUBMIT_DETAILS_SUCCESS,
        SUBMIT_DETAILS_FAIL
      ],
      promise: client => client.amendProfile(firstName, lastName, phone),
      onSuccessDispatched: () => {
        dispatch(showSnackbar('Details saved'));
      }
    });
  };
};

export const submitEmail = () => {
  return (
    dispatch: (ClientAction | Action) => void,
    getState: () => GlobalState
  ) => {
    const { profile } = getState();
    const { newEmail, password } = profile.user;
    const errors = validateEmailData(profile.user);

    const errorMessages = getErrors(errors);

    if (errorMessages) {
      dispatch(setValidationErrors(errorMessages));
      return;
    }

    dispatch({
      types: [SUBMIT_EMAIL_STARTED, SUBMIT_EMAIL_SUCCESS, SUBMIT_EMAIL_FAIL],
      promise: client => client.changeEmail(newEmail, password),
      onSuccessDispatched: () => {
        dispatch(showSnackbar('Confirmation email has been sent'));
      }
    });
  };
};

export const sendEmailVerificationLink = () => {
  return (dispatch: (ClientAction | Action) => void) => {
    dispatch({
      types: [
        EMAIL_VERIFICATION_REQUEST_SENT,
        EMAIL_VERIFICATION_REQUEST_SUCCESS,
        EMAIL_VERIFICATION_REQUEST_FAIL
      ],
      promise: client => client.sendEmailVerification(),
      onSuccessDispatched: () => {
        dispatch(
          showSnackbar('Email verification link has been emailed to you')
        );
      }
    });
  };
};

export const submitAddress = () => {
  return (
    dispatch: (ClientAction | Action) => void,
    getState: () => GlobalState
  ) => {
    const {
      profile: {
        user: {
          address: {
            houseNo,
            line1,
            line2,
            line3,
            city,
            state,
            postcode,
            country
          }
        }
      }
    } = getState();

    dispatch({
      types: [
        SUBMIT_ADDRESS_STARTED,
        SUBMIT_ADDRESS_SUCCESS,
        SUBMIT_ADDRESS_FAIL
      ],
      promise: client =>
        client.createUpdateAddress({
          houseNo,
          address1: line1,
          address2: line2,
          address3: line3,
          city,
          state,
          postalCode: postcode,
          country
        }),
      onSuccessDispatched: () => {
        dispatch(showSnackbar('Address saved'));
      }
    });
  };
};
